import classnames from 'classnames';
import produce from 'immer';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useLocation, useNavigate } from 'react-router-dom';
import PermissionChecker from '~/components/permission_checker';
import Permissions from '~/settings/permissions';
import { diffText } from '~/utils/diff';
import { Comments } from '../comments';
import { useComparisonGrid } from '../providers/comparison_grid';
import CellDropdown from './cell-dropdown';
import CellTabs from './cell-tabs';
import SingleDocumentModal from './singleDocumentModal';
const Cell = (props) => {
    var _a, _b;
    const { cellId, rowIndex, tagRowName, doc } = props;
    const navigate = useNavigate();
    const location = useLocation();
    const { commentId } = useParams();
    const { anchorDoc, selectedRowIndex, setSelectedRowIndex, anchorDocCellSelection, setAnchorDocCellSelection, areDiffsVisible, areCommentsVisible, } = useComparisonGrid();
    const [tabIndex, setTabIndex] = useState(0);
    const [classificationIndex, setClassificationIndex] = useState(0);
    const tabs = doc.classificationMap.get(tagRowName) || [];
    const anchorTabs = anchorDoc.classificationMap.get(tagRowName) || [];
    const isActiveRow = selectedRowIndex === rowIndex;
    const isAnchorCell = anchorDoc.documentComparisonFileId === doc.documentComparisonFileId;
    const currClassification = tabs[tabIndex] && tabs[tabIndex].classifications[classificationIndex]
        ? tabs[tabIndex].classifications[classificationIndex]
        : undefined;
    const [currentDiffText, setCurrentDiffText] = useState('');
    const [isModalVisible, setIsModalVisible] = useState(false);
    useEffect(() => {
        setTabIndex(0);
        setClassificationIndex(0);
    }, [anchorDoc]);
    useEffect(() => {
        var _a, _b, _c, _d, _e, _f;
        if (!isAnchorCell && areDiffsVisible) {
            let anchorTabIndex = 0;
            let anchorClassificationIndex = 0;
            const anchorCellSelection = anchorDocCellSelection.get(tagRowName);
            if (anchorCellSelection) {
                anchorTabIndex = anchorCellSelection.tabIndex;
                anchorClassificationIndex = anchorCellSelection.classificationIndex;
            }
            setCurrentDiffText(diffText(((_c = (_b = (_a = anchorTabs[anchorTabIndex]) === null || _a === void 0 ? void 0 : _a.classifications) === null || _b === void 0 ? void 0 : _b[anchorClassificationIndex]) === null || _c === void 0 ? void 0 : _c.text) || '', ((_f = (_e = (_d = tabs[tabIndex]) === null || _d === void 0 ? void 0 : _d.classifications) === null || _e === void 0 ? void 0 : _e[classificationIndex]) === null || _f === void 0 ? void 0 : _f.text) || ''));
        }
    }, [areDiffsVisible, tabIndex, classificationIndex, anchorDocCellSelection]);
    const singleTabWithAnchorName = tabs.length === 1 &&
        anchorTabs.length === 1 &&
        anchorTabs[0].name;
    const isParentClauseInTab = singleTabWithAnchorName
        ? tabs[0].name !== anchorTabs[0].name
        : false;
    const diffCategories = anchorTabs.length &&
        ((_a = anchorTabs[0].classifications) === null || _a === void 0 ? void 0 : _a.length) &&
        currClassification &&
        (currClassification === null || currClassification === void 0 ? void 0 : currClassification.category) !== anchorTabs[0].classifications[0].category;
    const similarityMatch = !isAnchorCell ? currClassification === null || currClassification === void 0 ? void 0 : currClassification.similarityMatch : undefined;
    const isCommentParamInCell = (commentId && ((_b = currClassification === null || currClassification === void 0 ? void 0 : currClassification.comments) === null || _b === void 0 ? void 0 : _b.find(x => x.id === commentId)));
    if (isCommentParamInCell) {
        if (selectedRowIndex === -1)
            setSelectedRowIndex(rowIndex);
        else if (selectedRowIndex === rowIndex) {
            const el = document.getElementById(`comment-${commentId}`);
            if (el)
                el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        }
        else if (selectedRowIndex !== rowIndex) {
            const splitPathname = location.pathname.split('/');
            splitPathname.pop();
            navigate(splitPathname.join('/'), { replace: true });
        }
    }
    const rowClass = classnames('classification cell', {
        active: isActiveRow,
        'param-comment': isCommentParamInCell && selectedRowIndex === rowIndex,
        'comments-visible': areCommentsVisible,
    });
    let minusHeight = 0;
    if (tabs.length > 1 || isParentClauseInTab) {
        minusHeight++;
    }
    if (tabs[tabIndex] && tabs[tabIndex].classifications.length > 1) {
        minusHeight++;
    }
    if (diffCategories) {
        minusHeight++;
    }
    if (similarityMatch) {
        minusHeight++;
    }
    const contentClass = classnames('clause-content', {
        'minus-3': minusHeight === 3,
        'minus-2': minusHeight === 2,
        'minus-1': minusHeight === 1,
    });
    const showDiff = !isAnchorCell && areDiffsVisible && isActiveRow;
    return (React.createElement("div", { className: rowClass, "data-testid": `cell-${cellId}`, onClick: () => !isActiveRow && setSelectedRowIndex(rowIndex) },
        React.createElement("span", { className: 'expand-cell-icon', onClick: () => setIsModalVisible(!isModalVisible), title: 'Expand This Clause', "data-testid": `cell-expand-${cellId}`, key: `cell-expand-${cellId}` },
            React.createElement("i", { className: 'fas fa-expand-arrows-alt' })),
        (tabs.length > 1 || isParentClauseInTab) && React.createElement(CellTabs, { tabs: tabs, activeTabIndex: tabIndex, onTabChange: (selectedIndex) => {
                setTabIndex(selectedIndex);
                setClassificationIndex(0);
                if (isAnchorCell) {
                    setAnchorDocCellSelection(produce(anchorDocCellSelection, draft => {
                        draft.set(tagRowName, { tabIndex: selectedIndex, classificationIndex: 0 });
                    }));
                }
            } }),
        !!(diffCategories) &&
            React.createElement("div", { className: 'clause-diff-category' }, currClassification === null || currClassification === void 0 ? void 0 :
                currClassification.category,
                "\u00A0",
                React.createElement("span", { className: 'diff-icon' },
                    React.createElement("i", { className: 'fas fa-exchange-alt' }))),
        !!similarityMatch &&
            React.createElement("div", { className: 'clause-similarity-match', "data-testid": `cell-similarity-${cellId}`, title: 'Aligned by semantic similarity to the anchor clause in this row: '
                    + `"${currClassification === null || currClassification === void 0 ? void 0 : currClassification.name}" (similarity ${similarityMatch.score.toFixed(2)})` },
                React.createElement("span", { className: 'similarity-match-score' },
                    React.createElement("i", { className: 'fas fa-sparkles similarity-match-icon' }),
                    similarityMatch.score.toFixed(2)),
                React.createElement("span", { className: 'similarity-match-tag' }, (currClassification === null || currClassification === void 0 ? void 0 : currClassification.descriptor) || (currClassification === null || currClassification === void 0 ? void 0 : currClassification.name))),
        (tabs[tabIndex] && tabs[tabIndex].classifications.length > 1) &&
            React.createElement(CellDropdown, { tab: tabs[tabIndex], onDropdownChange: (i) => {
                    setClassificationIndex(i);
                    if (isAnchorCell) {
                        setAnchorDocCellSelection(produce(anchorDocCellSelection, draft => {
                            draft.set(tagRowName, { tabIndex, classificationIndex: i });
                        }));
                    }
                } }),
        React.createElement("div", { className: contentClass, "data-testid": `cell-content-${cellId}` }, showDiff ? currentDiffText : currClassification === null || currClassification === void 0 ? void 0 : currClassification.text),
        tabs && isActiveRow && areCommentsVisible && (React.createElement(PermissionChecker, { requiredAllPermissions: [Permissions.VIEW_CLAUSE_COMMENTS] },
            React.createElement(Comments, { "data-testid": `cell-comments-${cellId}`, documentComparisonFileId: doc.documentComparisonFileId, classificationId: currClassification === null || currClassification === void 0 ? void 0 : currClassification.id, cellComments: currClassification === null || currClassification === void 0 ? void 0 : currClassification.comments }))),
        isModalVisible && React.createElement(SingleDocumentModal, { "data-testid": `cell-single-document-modal-${cellId}`, anchorDoc: anchorDoc, anchorClassification: anchorTabs, classification: currClassification, onHide: () => setIsModalVisible(false), visible: isModalVisible, comparisonDoc: doc, tabs: tabs, disableComments: false })));
};
export default Cell;
